<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { formatPoints } from "@/state/helpers/experiences";

// Vitrine de Experiências: cards com imagem, pontos e estado de resgate
export default {
  components: { Layout, Breadcrumb },
  data() {
    return {
      loading: true,
      errored: false,
      list: [],
      categories: [],
      balance: null,

      search: "",
      category: null,
      range: null,
      order: "news",
      affordable: false,

      rangeOptions: [
        { value: null, text: "Todas as faixas" },
        { value: "1", text: "Até 5.000 pts" },
        { value: "2", text: "5.001 a 10.000 pts" },
        { value: "3", text: "Acima de 10.000 pts" },
      ],
      orderOptions: [
        { value: "news", text: "Novidades" },
        { value: "lowest", text: "Menor pontuação" },
        { value: "highest", text: "Maior pontuação" },
      ],
    };
  },
  computed: {
    categoryOptions() {
      return [{ value: null, text: "Todas as categorias" }].concat(
        this.categories.map((row) => ({ value: row.id, text: row.name }))
      );
    },
    filtered() {
      const term = this.search.trim().toLowerCase();

      const list = this.list.filter((item) => {
        if (term && [item.title, item.partner].join(" ").toLowerCase().indexOf(term) === -1) {
          return false;
        }
        if (this.category && String(item.category.id) !== String(this.category)) {
          return false;
        }
        if (this.range === "1" && item.points > 5000) {
          return false;
        }
        if (this.range === "2" && (item.points <= 5000 || item.points > 10000)) {
          return false;
        }
        if (this.range === "3" && item.points <= 10000) {
          return false;
        }
        if (this.affordable && !item.canRedeem) {
          return false;
        }
        return true;
      });

      // Destaques primeiro; depois a ordenação escolhida
      return list.slice().sort((first, second) => {
        if (first.featured !== second.featured) {
          return first.featured ? -1 : 1;
        }
        if (this.order === "lowest") {
          return first.points - second.points;
        }
        if (this.order === "highest") {
          return second.points - first.points;
        }
        return 0;
      });
    },
  },
  methods: {
    formatPoints,
    load() {
      this.loading = true;
      this.errored = false;

      Promise.all([api.get("experiences"), api.get("experiences/categories")])
        .then(([experiences, categories]) => {
          if (experiences.data.status == "success") {
            this.list = experiences.data.list || [];
            this.balance = experiences.data.balance;
          } else {
            this.errored = true;
          }
          if (categories.data.status == "success") {
            this.categories = categories.data.list || [];
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: 'Experiências' }]" />

    <div class="ds-page">
      <div class="d-md-flex justify-content-between align-items-center mb-4">
        <div class="mb-3 mb-md-0">
          <h1 class="ds-title">Experiências</h1>
          <p class="ds-subtitle m-0">Troque seus pontos por experiências</p>
        </div>
        <div class="ex-balance">
          <small>Seu saldo</small>
          <strong>{{ balance === null ? '–' : formatPoints(balance) }}</strong>
        </div>
      </div>

      <div v-if="loading" class="ex-grid">
        <div v-for="index in 6" :key="index" class="ex-skeleton"></div>
      </div>
      <div v-else-if="errored" class="ds-box ds-result text-center">
        <i class="bx bx-error-circle ds-result-icon" aria-hidden="true"></i>
        <p>Pedimos desculpas, não podemos recuperar as informações neste momento.</p>
        <button class="btn btn-default" v-on:click="load()">Tentar novamente</button>
      </div>
      <div v-else-if="list.length == 0" class="ds-box ds-result text-center">
        <i class="bx bx-gift ds-result-icon" aria-hidden="true"></i>
        <h4>Nenhuma experiência disponível no momento</h4>
        <p class="text-muted mb-0">Volte em breve para conferir as novidades.</p>
      </div>
      <template v-else>
        <div class="ds-box ds-box-wide">
          <div class="row">
            <div class="col-lg-4 mb-2">
              <b-form-input v-model="search" type="text" placeholder="Buscar experiência"></b-form-input>
            </div>
            <div class="col-lg-2 col-md-4 mb-2">
              <b-form-select v-model="category" :options="categoryOptions"></b-form-select>
            </div>
            <div class="col-lg-2 col-md-4 mb-2">
              <b-form-select v-model="range" :options="rangeOptions"></b-form-select>
            </div>
            <div class="col-lg-2 col-md-4 mb-2">
              <b-form-select v-model="order" :options="orderOptions"></b-form-select>
            </div>
            <div class="col-lg-2 mb-2 d-flex align-items-center">
              <b-form-checkbox v-model="affordable" switch>Posso resgatar</b-form-checkbox>
            </div>
          </div>
        </div>

        <div v-if="filtered.length == 0" class="ds-box ds-result text-center">
          <i class="bx bx-search ds-result-icon" aria-hidden="true"></i>
          <p class="mb-0">Nenhuma experiência encontrada.</p>
        </div>
        <div v-else class="ex-grid">
          <div v-for="item in filtered" :key="item.id" class="ex-card">
            <div class="ex-badges">
              <span v-if="item.featured" class="badge badge-default font-size-11 text-uppercase mr-1">Destaque</span>
              <span v-if="item.soldOut" class="badge badge-dark font-size-11 text-uppercase">Indisponível</span>
            </div>

            <router-link tag="a" :to="'/experiences/' + item.id" class="ex-cover">
              <img v-if="item.image" :src="item.image" :alt="item.title">
              <span v-else class="ex-cover-empty"><i class="bx bx-gift" aria-hidden="true"></i></span>
            </router-link>

            <div class="ex-card-body">
              <h2 class="ex-card-title">{{ item.title }}</h2>
              <div class="ex-card-meta">{{ [item.partner, item.city].filter(Boolean).join(' · ') }}</div>
              <div class="ex-card-points">{{ formatPoints(item.points) }}</div>
              <div v-if="!item.soldOut && item.missingPoints > 0" class="ex-card-missing">Faltam {{ formatPoints(item.missingPoints) }}</div>
              <router-link tag="a" :to="'/experiences/' + item.id" class="btn btn-default btn-block text-uppercase">Ver mais</router-link>
            </div>
          </div>
        </div>
      </template>
    </div>
  </Layout>
</template>
